import React from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { colors } from '@/design-system/theme/colors';
import { CreditStatus } from '@/types';
import { useTranslation } from '@/hooks/useTranslation';

interface CreditStatusBadgeProps {
  creditStatus: CreditStatus | null;
  isLoading?: boolean;
  compact?: boolean;
  onPress?: () => void;
}

/**
 * CreditStatusBadge - Displays user's available credits
 *
 * Display logic:
 * - B2C users: Shows total available credits with icon
 * - B2B2C Students: Hidden (usage_hidden: true)
 * - B2B2C Admins: Would show school pool (handled separately)
 * - Warning state when low_credit_warning is true
 */
export function CreditStatusBadge({
  creditStatus,
  isLoading = false,
  compact = false,
  onPress,
}: CreditStatusBadgeProps) {
  const { t } = useTranslation();

  // Don't render for B2B2C students (usage is hidden)
  if (creditStatus?.usage_hidden) {
    return null;
  }

  // Credit-exempt users (super-admins) see an "Unlimited" badge
  if (creditStatus?.credit_exempt) {
    const unlimitedContent = (
      <View nativeID="credit_badge" className="flex-row items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200">
        <MaterialIcons name="all-inclusive" size={compact ? 14 : 16} color={colors.success ?? '#059669'} />
        <Text className={`${compact ? 'text-xs' : 'text-sm'} font-semibold text-emerald-700`}>
          {compact ? '∞' : t('dashboard.unlimited')}
        </Text>
      </View>
    );
    if (onPress) {
      return (
        <TouchableOpacity onPress={onPress} activeOpacity={0.7}>
          {unlimitedContent}
        </TouchableOpacity>
      );
    }
    return unlimitedContent;
  }

  // Loading state
  if (isLoading) {
    return (
      <View className="flex-row items-center gap-1 px-2 py-1 rounded-full bg-gray-100">
        <ActivityIndicator size="small" color={colors.gray[500]} />
        {!compact && <Text className="text-xs text-gray-500">{t('common.loading')}</Text>}
      </View>
    );
  }

  // No credit status available
  if (!creditStatus) {
    return null;
  }

  const totalAvailable = creditStatus.total_available ?? 0;
  const isLowCredit = creditStatus.low_credit_warning ?? false;
  const isZeroCredits = totalAvailable === 0;

  // Determine badge styling based on credit state
  const getBadgeStyle = () => {
    if (isZeroCredits) {
      return {
        bg: 'bg-red-50 border border-red-200',
        textColor: 'text-red-700',
        iconColor: colors.error,
      };
    }
    if (isLowCredit) {
      return {
        bg: 'bg-amber-50 border border-amber-200',
        textColor: 'text-amber-700',
        iconColor: colors.warning,
      };
    }
    return {
      bg: 'bg-blue-50 border border-blue-200',
      textColor: 'text-blue-700',
      iconColor: colors.primary,
    };
  };

  const style = getBadgeStyle();

  const content = (
    <View nativeID="credit_badge" className={`flex-row items-center gap-1.5 px-3 py-1.5 rounded-full ${style.bg}`}>
      <MaterialIcons
        name={isZeroCredits ? 'error-outline' : isLowCredit ? 'warning' : 'toll'}
        size={compact ? 14 : 16}
        color={style.iconColor}
      />
      <Text className={`${compact ? 'text-xs' : 'text-sm'} font-semibold ${style.textColor}`}>
        {totalAvailable} {compact ? '' : totalAvailable === 1 ? t('dashboard.credit') : t('dashboard.creditPlural')}
      </Text>
      {onPress && !compact && (
        <MaterialIcons name="chevron-right" size={16} color={style.iconColor} />
      )}
    </View>
  );

  if (onPress) {
    return (
      <TouchableOpacity onPress={onPress} activeOpacity={0.7}>
        {content}
      </TouchableOpacity>
    );
  }

  return content;
}

export default CreditStatusBadge;
